/**
 * The four manual save slots, rendered once and reused everywhere they appear.
 *
 * Two callers, two modes:
 *   - `'menu'`   — the Main Menu's Saves screen. Occupied slots offer Load + Delete; there is no
 *                  way to write a save from here (the live autosave is the only thing that writes).
 *   - `'ingame'` — the in-game Save menu overlay. Empty slots offer "Save here" (write the current
 *                  run, no confirm); occupied slots offer Load (behind an unsaved-loss confirm) and
 *                  Delete.
 *
 * There is deliberately **no overwrite of an occupied slot** in either mode — saving only ever
 * writes to an empty slot. This matches the decided model: the four slots are durable checkpoints,
 * and the way to keep an old one while making a new one is to save into a different (empty) slot.
 *
 * The rendering, the inline two-tap confirm, and the busy-lock are shared here so the two callers
 * cannot drift apart; the `nb-slot*` styles are the same ones the Saves screen has always used.
 */
import { h } from '../dom.js';
import { t } from '../../i18n/index.js';
import { api } from '../../api/client.js';
import type { ProgressSync } from '../../state/progress.js';
import type { SaveSlotMeta } from '../../api/types.js';

export type SaveSlotsMode = 'menu' | 'ingame';

export interface SaveSlotsOptions {
  mode: SaveSlotsMode;
  sync: ProgressSync;
  /**
   * Load a slot into the active game and enter it. Must route through the app so the game is
   * rebuilt from the loaded save. Present for both modes.
   */
  onLoadSlot: (slot: number) => Promise<void>;
}

export interface SaveSlots {
  element: HTMLElement;
  /** Read the slots from the server and repaint. Call when the surface is shown. */
  refresh: () => void;
}

const SLOTS = [1, 2, 3, 4] as const;

/**
 * Which actions a mode offers, given whether a slot is occupied. Pure — exported so the gating is
 * testable without a DOM: an empty slot in `'menu'` mode has no actions, an occupied slot never
 * offers "save", and only `'ingame'` empty slots offer "save".
 */
export type SlotAction = 'save' | 'load' | 'delete';

export function slotActions(mode: SaveSlotsMode, occupied: boolean): SlotAction[] {
  if (occupied) return ['load', 'delete'];
  // Empty: the Saves screen offers nothing; the in-game menu offers a plain save.
  return mode === 'ingame' ? ['save'] : [];
}

export function createSaveSlots({ mode, sync, onLoadSlot }: SaveSlotsOptions): SaveSlots {
  const list = h('div', { class: 'nb-slots' });
  const status = h('p', { class: 'nb-hint', 'aria-live': 'polite' });
  let busy = false;

  const setBusy = (on: boolean): void => {
    busy = on;
    list.querySelectorAll('button').forEach((b) => ((b as HTMLButtonElement).disabled = on));
  };

  const summaryFor = (meta: SaveSlotMeta): string => {
    const parts = [t('menu.slot.clues', { held: meta.clueCount, total: meta.clueTotal })];
    if (meta.endingId) parts.push(t('menu.slot.resolved'));
    parts.push(t('menu.slot.saved', { date: formatWhen(meta.updatedAt) }));
    return parts.join(' · ');
  };

  const renderSlots = (metas: SaveSlotMeta[]): void => {
    const bySlot = new Map(metas.map((m) => [m.slot, m]));
    list.replaceChildren(...SLOTS.map((slot) => renderSlotCard(slot, bySlot.get(slot) ?? null)));
  };

  const renderSlotCard = (slot: number, meta: SaveSlotMeta | null): HTMLElement => {
    const occupied = meta !== null;
    const actions = h('div', { class: 'nb-slot__actions' });

    const meta_line = h('span', {
      class: 'nb-slot__meta',
      text: occupied ? summaryFor(meta!) : t('menu.slot.empty'),
    });

    // A small inline confirm, so a destructive tap always takes two.
    const confirmThen = (question: string, run: () => void | Promise<void>): void => {
      const bar = h(
        'div',
        { class: 'nb-slot__confirm', role: 'alertdialog', 'aria-label': question },
        h('span', { class: 'nb-slot__confirm-q', text: question }),
        h('button', {
          class: 'nb-button nb-button--alert nb-button--small',
          type: 'button',
          text: t('menu.slot.confirmYes'),
          onclick: () => {
            bar.remove();
            void run();
          },
        }),
        h('button', {
          class: 'nb-button nb-button--quiet nb-button--small',
          type: 'button',
          text: t('menu.slot.confirmNo'),
          onclick: () => bar.remove(),
        }),
      );
      actions.replaceChildren(bar);
    };

    const doSave = async (): Promise<void> => {
      setBusy(true);
      status.textContent = t('menu.slot.saving');
      try {
        await api.saveToSlot(
          slot,
          sync.progress.get() as unknown as Record<string, unknown>,
          sync.summaryProvider?.(),
        );
        status.textContent = '';
        const fresh = await api.listSaves();
        renderSlots(fresh.slots);
      } catch {
        status.textContent = t('menu.slot.saveError');
      } finally {
        setBusy(false);
      }
    };

    const doDelete = async (): Promise<void> => {
      setBusy(true);
      try {
        await api.deleteSlot(slot);
        const fresh = await api.listSaves();
        renderSlots(fresh.slots);
      } catch {
        status.textContent = t('menu.saves.error');
      } finally {
        setBusy(false);
      }
    };

    const doLoad = (): void => {
      setBusy(true);
      status.textContent = t('menu.slot.loading');
      void onLoadSlot(slot).catch(() => {
        status.textContent = t('menu.slot.loadError');
        setBusy(false);
      });
    };

    const builders: Record<SlotAction, () => HTMLElement> = {
      // Empty-slot save, in-game only. No confirm — writing an empty slot destroys nothing.
      save: () =>
        h('button', {
          class: 'nb-button nb-button--small',
          type: 'button',
          text: t('menu.slot.save'),
          onclick: () => {
            if (busy) return;
            void doSave();
          },
        }),
      // Loading always discards the live unsaved run. The Saves screen loads on a single tap
      // (there is nothing in progress to protect from the menu); in-game it confirms first.
      load: () =>
        h('button', {
          class: 'nb-button nb-button--primary nb-button--small',
          type: 'button',
          text: t('menu.slot.load'),
          onclick: () => {
            if (busy) return;
            if (mode === 'ingame') confirmThen(t('saves.confirmLoad'), doLoad);
            else doLoad();
          },
        }),
      delete: () =>
        h('button', {
          class: 'nb-button nb-button--quiet nb-button--small',
          type: 'button',
          text: t('menu.slot.delete'),
          onclick: () => {
            if (busy) return;
            confirmThen(t('menu.slot.confirmDelete', { n: slot }), doDelete);
          },
        }),
    };

    actions.replaceChildren(...slotActions(mode, occupied).map((action) => builders[action]()));

    return h(
      'article',
      { class: 'nb-slot', 'data-occupied': String(occupied) },
      h(
        'div',
        { class: 'nb-slot__head' },
        h('h3', { class: 'nb-slot__title', text: t('menu.slot.title', { n: slot }) }),
        meta_line,
      ),
      actions,
    );
  };

  const refresh = (): void => {
    status.textContent = t('menu.saves.loading');
    // Paint empty placeholders immediately so the layout doesn't jump.
    renderSlots([]);
    void api
      .listSaves()
      .then((res) => {
        status.textContent = '';
        renderSlots(res.slots);
      })
      .catch(() => {
        status.textContent = t('menu.saves.error');
      });
  };

  const element = h('div', { class: 'nb-slots__wrap' }, list, status);

  return { element, refresh };
}

function formatWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
